import React from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { paymentsApi, devicesApi, LOW_REFILL_THRESHOLD } from '../api'
import useFetch from '../hooks/useFetch'
import {
  Page, PageHead, Card, StatGrid, Stat, TableWrapper, Table, StatusPill,
  Loading, ErrorState, EmptyState,
} from '../Components/ui'
import { colors } from '../theme'

const SectionTitle = styled.h3`
  margin: 0 0 1rem;
  color: ${colors.ink};
  font-size: 1.15rem;
`

const AlertCard = styled.div`
  background: ${colors.redLight};
  border: 1px solid ${colors.red}33;
  border-left: 5px solid ${colors.red};
  border-radius: 14px;
  padding: 1.1rem 1.4rem;
  margin-bottom: 1.5rem;

  h3 { margin: 0 0 0.4rem; color: ${colors.red}; font-size: 1rem; }
  ul { margin: 0; padding-left: 1.1rem; }
  li { color: ${colors.ink}; font-size: 0.9rem; line-height: 1.55; }
`

const isToday = (d) => {
  if (!d) return false
  const x = new Date(d)
  const now = new Date()
  return x.toDateString() === now.toDateString()
}

const money = (n) => `KES ${Number(n || 0).toLocaleString()}`
const litres = (ml) => `${(Number(ml || 0) / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })} L`
const fmtDate = (d) => (d ? new Date(d).toLocaleString() : '—')

const Dashboard = () => {
  const { data, loading, error, reload } = useFetch(
    () => Promise.all([paymentsApi.list(), devicesApi.list()]),
    []
  )

  const [payments = [], devices = []] = data || []

  const completed = payments.filter((p) => p.statusState === 'success')
  const kpis = {
    activeDevices: devices.filter((d) => d.statusState === 'success').length,
    totalDevices: devices.length,
    revenueToday: completed.filter((p) => isToday(p.date)).reduce((s, p) => s + p.amount, 0),
    pending: payments.filter((p) => p.statusState === 'pending').length,
    litres: completed.reduce((s, p) => s + p.units, 0),
    completedCount: completed.length,
  }

  const lowRefill = devices.filter(
    (d) => d.refillLevel != null && d.refillLevel <= LOW_REFILL_THRESHOLD
  )

  const recent = payments.slice(0, 6)

  return (
    <Page>
      <PageHead>
        <h1>Dashboard Overview</h1>
        <p>Monitor dispensers, payment activity, and detergent volume in real time.</p>
      </PageHead>

      {loading && <Card><Loading label="Loading dashboard…" /></Card>}
      {error && <Card><ErrorState message={error.message} onRetry={reload} /></Card>}

      {!loading && !error && (
        <>
          <StatGrid cols={4}>
            <Stat label="Active Dispensers" value={kpis.activeDevices}
              sub={`${kpis.totalDevices} registered`} />
            <Stat label="Revenue Today" value={money(kpis.revenueToday)}
              sub={`${kpis.pending} pending`} />
            <Stat label="Detergent Dispensed" value={litres(kpis.litres)}
              sub="from completed payments" />
            <Stat label="Low-refill Alerts" value={lowRefill.length}
              sub={`≤ ${LOW_REFILL_THRESHOLD}% remaining`} />
          </StatGrid>

          {lowRefill.length > 0 && (
            <AlertCard>
              <h3>Dispensers needing a refill</h3>
              <ul>
                {lowRefill.map((d) => (
                  <li key={d.id}>
                    {d.name}{d.location ? ` — ${d.location}` : ''}: {d.refillLevel}% remaining
                  </li>
                ))}
              </ul>
            </AlertCard>
          )}

          <Card>
            <SectionTitle>Recent Payments</SectionTitle>
            {recent.length === 0 ? (
              <EmptyState title="No payments yet"
                message="Transactions will appear here once the dispenser processes M-Pesa payments." />
            ) : (
              <TableWrapper>
                <Table min="640px">
                  <thead>
                    <tr>
                      <th>Receipt</th>
                      <th>Phone</th>
                      <th>Amount</th>
                      <th>Volume</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent.map((p) => (
                      <tr key={p.id}>
                        <td>{p.mpesaCode}</td>
                        <td>{p.phone}</td>
                        <td>{money(p.amount)}</td>
                        <td>{litres(p.units)}</td>
                        <td><StatusPill state={p.statusState} label={p.statusLabel} /></td>
                        <td>{fmtDate(p.date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrapper>
            )}
            <div style={{ marginTop: '1rem' }}>
              <Link to="/payments" style={{ color: colors.green, fontWeight: 700 }}>
                View all payments →
              </Link>
            </div>
          </Card>
        </>
      )}
    </Page>
  )
}

export default Dashboard
